/**
 * Partner Usage Dashboard — Org Admin view of per-partner credit consumption.
 *
 * Features per plan Task 8.5:
 * - Table: partner name, credits used, soft cap, % of cap, campaigns
 * - Bar chart: cumulative consumption visualization per partner
 * - Drill-down: click partner → campaign-level usage log
 *
 * Fetches from GET /api/v1/credits/usage and GET /api/v1/credits/usage/partner/{id}.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../lib/api';

interface PartnerUsage {
  partition_id: string;
  partition_name: string;
  credits_used: number;
  soft_cap: number | null;
  cap_pct: number | null;
  campaign_count: number;
  enforcement: string | null;
}

interface PartnerDetail {
  credits_used_this_quarter: number;
  soft_cap: number | null;
  enforcement: string | null;
  campaign_usage?: Array<{
    campaign_id: string | null;
    campaign_name: string;
    credits_used: number;
  }>;
}

interface DailyUsagePoint {
  date: string;
  total: number;
  asset_pack?: number;
  localization?: number;
}

interface DepletionProjection {
  run_rate_credits_per_day: number;
  projected_depletion_date: string | null;
  current_available_credits: number;
}

export const PartnerUsageDashboard: React.FC = () => {
  const [partners, setPartners] = useState<PartnerUsage[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyUsagePoint[]>([]);
  const [eventBreakdown, setEventBreakdown] = useState<Record<string, number>>({});
  const [personaBreakdown, setPersonaBreakdown] = useState<Array<{ persona_name: string; credits_used: number }>>([]);
  const [depletionProjection, setDepletionProjection] = useState<DepletionProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [partnerDetail, setPartnerDetail] = useState<PartnerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiFetch('/api/v1/credits/usage');
        setPartners(data.partners || []);
        setDailyTrend(data.daily_usage_trend || data.trend || []);
        setEventBreakdown(data.event_type_breakdown || {});
        setPersonaBreakdown(data.persona_breakdown || []);
        setDepletionProjection(data.depletion_projection || null);
      } catch {
        // Silently fail — dashboard widget
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePartnerClick = useCallback(async (partitionId: string) => {
    if (selectedPartner === partitionId) {
      setSelectedPartner(null);
      setPartnerDetail(null);
      return;
    }
    setSelectedPartner(partitionId);
    setDetailLoading(true);
    try {
      const data = await apiFetch(`/api/v1/credits/usage/partner/${partitionId}`);
      setPartnerDetail(data);
    } catch {
      setPartnerDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedPartner]);

  if (loading) {
    return <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />;
  }

  if (partners.length === 0) {
    return (
      <div className="text-sm text-gray-500 p-4 text-center">
        No partner usage data this quarter
      </div>
    );
  }

  const trendPoints = dailyTrend.map((point) => Number(point.total || 0));
  const chartMax = Math.max(1, ...trendPoints);
  const trendPath = trendPoints
    .map((value, idx) => {
      const x = trendPoints.length > 1 ? (idx / (trendPoints.length - 1)) * 100 : 0;
      const y = 36 - (value / chartMax) * 32;
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="text-sm font-medium text-gray-700">Partner Credit Usage</h3>
      </div>

      {/* Cumulative line chart */}
      <div className="px-4 py-3 border-b">
        {dailyTrend.length === 0 ? (
          <div className="text-xs text-gray-500">No trend data available this quarter.</div>
        ) : (
          <div className="space-y-3">
            <svg viewBox="0 0 100 40" className="w-full h-40 bg-gray-50 rounded border">
              <path d={trendPath} fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <div className="grid gap-2 md:grid-cols-3 text-xs text-gray-600">
              <div>
                <p className="font-medium text-gray-700">Asset Pack</p>
                <p>{Number(eventBreakdown.asset_pack || 0).toFixed(2)} credits</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Localization</p>
                <p>{Number(eventBreakdown.localization || 0).toFixed(2)} credits</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Projected Depletion</p>
                <p>{depletionProjection?.projected_depletion_date || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-b bg-gray-50/50">
        <div className="text-xs text-gray-600 flex flex-wrap gap-4">
          <span>
            Run rate: <strong>{Number(depletionProjection?.run_rate_credits_per_day || 0).toFixed(2)}</strong> credits/day
          </span>
          <span>
            Available now: <strong>{Number(depletionProjection?.current_available_credits || 0).toFixed(2)}</strong>
          </span>
          <span>
            Top persona: <strong>{personaBreakdown[0]?.persona_name || 'N/A'}</strong>
          </span>
        </div>
      </div>

      {/* Detail table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-gray-500 uppercase">
              <th className="px-4 py-2">Partner</th>
              <th className="px-4 py-2 text-right">Credits Used</th>
              <th className="px-4 py-2 text-right">Soft Cap</th>
              <th className="px-4 py-2 text-right">% of Cap</th>
              <th className="px-4 py-2 text-right">Campaigns</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => {
              const isOverCap = p.cap_pct !== null && p.cap_pct > 100;
              const isNearCap = p.cap_pct !== null && p.cap_pct >= 80 && p.cap_pct <= 100;
              const isSelected = selectedPartner === p.partition_id;

              return (
                <React.Fragment key={p.partition_id}>
                  <tr
                    className={`border-b last:border-0 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handlePartnerClick(p.partition_id)}
                  >
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {p.partition_name}
                      <span className="text-xs text-gray-400 ml-1">
                        {isSelected ? '▼' : '▶'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {p.credits_used}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">
                      {p.soft_cap ?? '—'}
                    </td>
                    <td className={`px-4 py-2 text-right font-medium ${
                      isOverCap ? 'text-red-600' :
                      isNearCap ? 'text-amber-600' :
                      'text-gray-600'
                    }`}>
                      {p.cap_pct !== null ? `${p.cap_pct}%` : '—'}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {p.campaign_count}
                    </td>
                  </tr>

                  {/* Drill-down detail row */}
                  {isSelected && (
                    <tr className="bg-blue-50/50">
                      <td colSpan={5} className="px-4 py-3">
                        {detailLoading ? (
                          <div className="text-xs text-gray-500 animate-pulse">Loading details...</div>
                        ) : partnerDetail ? (
                          <div className="text-xs text-gray-600 space-y-2">
                            <div className="flex gap-6">
                              <span>Credits this quarter: <strong>{partnerDetail.credits_used_this_quarter}</strong></span>
                              <span>Cap: <strong>{partnerDetail.soft_cap ?? 'None'}</strong></span>
                              <span>Enforcement: <strong>{partnerDetail.enforcement ?? 'N/A'}</strong></span>
                            </div>
                            {partnerDetail.campaign_usage && partnerDetail.campaign_usage.length > 0 ? (
                              <div className="bg-white border rounded overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead className="bg-gray-50 text-gray-500">
                                    <tr>
                                      <th className="px-2 py-1 text-left">Campaign</th>
                                      <th className="px-2 py-1 text-right">Credits Used</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {partnerDetail.campaign_usage.map((campaign) => (
                                      <tr key={campaign.campaign_id || campaign.campaign_name} className="border-t">
                                        <td className="px-2 py-1 text-gray-700">{campaign.campaign_name}</td>
                                        <td className="px-2 py-1 text-right font-medium text-gray-800">
                                          {campaign.credits_used}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-gray-400 mt-1">No campaign-level usage available.</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">No details available</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartnerUsageDashboard;
